// src/components/opportunity/OpportunityPricingFormRedIron.tsx
//
// Red-Iron pricing form. Extracted from `OpportunityPricingTab.tsx` in
// slice B (opp engine iter 2) so the tab itself is a thin format
// dispatcher and each format-specific form stays under the file-size
// hygiene cap.
//
// This file owns:
//   - The 5 cost line items + additional items list.
//   - The three percentage fields (commission, margin, deposit).
//   - The "live totals" card on the right.
//   - The revision notes field + the save button.
//
// State management stays in the parent so the dispatcher can swap
// formats without losing keystrokes if a user toggles the product type
// mid-edit (today the format is derived from product_type and never
// flips, but the prop-down pattern leaves the door open).

import { useMemo } from "react";
import type {
  Opportunity,
  OpportunityPricingTotals,
} from "@/types/opportunity";
import { computePricingTotals } from "@/lib/opportunity/calc";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import {
  makeAdditionalItem,
  pricingInputsFromForm,
  type OpportunityPricingFormItem,
  type OpportunityPricingFormState,
} from "@/components/opportunity/OpportunityPricingForm";
import {
  MoneyField,
  PctField,
  TotalsCard,
  pctDelta,
} from "@/components/opportunity/OpportunityPricingFields";
import { OpportunitySalesAdjustmentsSection } from "@/components/opportunity/OpportunitySalesAdjustmentsSection";

export type OpportunityPricingFormRedIronProps = {
  opportunity: Opportunity;
  form: OpportunityPricingFormState;
  setForm: (next: OpportunityPricingFormState) => void;
  /** Render-prop slot for the save button + status messages on the right column. */
  renderSavePanel: () => React.ReactNode;
};

export function OpportunityPricingFormRedIron(
  props: OpportunityPricingFormRedIronProps,
) {
  const { form, setForm, renderSavePanel } = props;
  const org = useOrgSettings();

  const inputs = useMemo(() => pricingInputsFromForm(form), [form]);
  const totals: OpportunityPricingTotals = useMemo(
    () => computePricingTotals(inputs),
    [inputs],
  );

  function update<K extends keyof OpportunityPricingFormState>(
    key: K,
    value: OpportunityPricingFormState[K],
  ) {
    setForm({ ...form, [key]: value });
  }

  function updateItem(key: string, patch: Partial<OpportunityPricingFormItem>) {
    setForm({
      ...form,
      additionalItems: form.additionalItems.map((it) =>
        it.key === key ? { ...it, ...patch } : it,
      ),
    });
  }

  function addItem() {
    setForm({ ...form, additionalItems: [...form.additionalItems, makeAdditionalItem()] });
  }

  function removeItem(key: string) {
    setForm({
      ...form,
      additionalItems: form.additionalItems.filter((it) => it.key !== key),
    });
  }

  const commissionDelta = pctDelta(form.commissionPct, org.defaultCommissionPct);
  const marginDelta = pctDelta(form.marginPct, org.defaultMarginPct);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
      <div className="space-y-4">
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-3">
            Cost line items
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MoneyField
              label="Building materials"
              value={form.buildingMaterialsCost}
              onChange={(v) => update("buildingMaterialsCost", v)}
            />
            <MoneyField
              label="Erection labor"
              value={form.erectionCost}
              onChange={(v) => update("erectionCost", v)}
            />
            <MoneyField
              label="Concrete"
              value={form.concreteCost}
              onChange={(v) => update("concreteCost", v)}
            />
            <MoneyField
              label="Engineering"
              value={form.engineeringCost}
              onChange={(v) => update("engineeringCost", v)}
            />
            <MoneyField
              label="Permit assistance"
              value={form.permitAssistanceCost}
              onChange={(v) => update("permitAssistanceCost", v)}
            />
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              Additional items
            </h4>
            <button
              type="button"
              onClick={addItem}
              className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              + Add line
            </button>
          </div>
          {form.additionalItems.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              No additional items. Use this for one-offs (e.g. site prep, optional
              upgrades).
            </p>
          ) : (
            <div className="space-y-2">
              {form.additionalItems.map((it) => (
                <div
                  key={it.key}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2 items-end"
                >
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                      Description
                    </label>
                    <input
                      value={it.label}
                      onChange={(e) => updateItem(it.key, { label: e.target.value })}
                      placeholder="e.g. Site prep"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                      Amount
                    </label>
                    <input
                      value={it.amount}
                      onChange={(e) => updateItem(it.key, { amount: e.target.value })}
                      placeholder="0.00"
                      inputMode="decimal"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 tabular-nums dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(it.key)}
                    className="px-2 py-2 rounded-lg text-xs text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <OpportunitySalesAdjustmentsSection form={form} setForm={setForm} />

        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-3">
            Percentages
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <PctField
              label="Commission %"
              value={form.commissionPct}
              onChange={(v) => update("commissionPct", v)}
              defaultPct={org.defaultCommissionPct}
              delta={commissionDelta}
            />
            <PctField
              label="Margin %"
              value={form.marginPct}
              onChange={(v) => update("marginPct", v)}
              defaultPct={org.defaultMarginPct}
              delta={marginDelta}
            />
            <PctField
              label="Deposit %"
              value={form.depositPct}
              onChange={(v) => update("depositPct", v)}
              defaultPct={25}
              delta={pctDelta(form.depositPct, 25)}
            />
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Revision notes
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={3}
            placeholder="What's changing in this revision? (e.g. 'Adjusted erection labor after site visit.')"
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
          />
        </section>
      </div>

      <aside className="space-y-3 lg:sticky lg:top-4 self-start">
        <TotalsCard
          totals={totals}
          commissionPct={inputs.commission_pct}
          marginPct={inputs.margin_pct}
          depositPct={inputs.deposit_pct}
        />
        {renderSavePanel()}
      </aside>
    </div>
  );
}
